import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchNotification } from "../services/api";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { AiOutlineClose } from "react-icons/ai";

dayjs.extend(relativeTime);

// The header used to hardcode "Dashboard" on every page.
const PAGE_TITLES = {
  "/dashboard": { title: "Dashboard", subtitle: "Let's check your update today" },
  "/report": { title: "Report", subtitle: "Build and export device reports" },
  "/users": { title: "Users", subtitle: "People with access to this tenant" },
  "/document": { title: "Document", subtitle: "Documents and templates" },
  "/graph": { title: "Graph", subtitle: "Telemetry trends over time" },
};

const Navbar = ({ onMenuClick = () => {} }) => {
  const [theme, setTheme] = useState(() => localStorage.theme || "light");
  const [notification, setnotification] = useState(false);
  const [profileActionShow, setprofileActionShow] = useState(false);
  const [noficationData, setnotificationData] = useState();
  const [userData, setUserData] = useState(null);

  const location = useLocation();
  const page = PAGE_TITLES[location.pathname.toLowerCase()] || {
    title: "TeraStamp",
    subtitle: "A Data Management Reporting Platform",
  };

  const notificationRef = useRef(null);
  const profileRef = useRef(null);

  // Apply the theme to <html> whenever it changes.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.theme = theme;
  }, [theme]);

  // Notifications are fetched once, not on every theme flip like before.
  useEffect(() => {
    fetchNotification()
      .then((res) => setnotificationData(res.data))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUserData(parsed.user);
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, []);

  // Clicking anywhere outside an open dropdown should dismiss it.
  useEffect(() => {
    const onDocClick = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setnotification(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setprofileActionShow(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  const notificationAction = () => setnotification((prev) => !prev);
  const profileAction = () => setprofileActionShow((prev) => !prev);

  const handleLogout = () => {
    // The stale user object used to be left behind on logout.
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  const initials = userData
    ? `${userData.first_name?.[0] || ""}${userData.last_name?.[0] || ""}`.toUpperCase() ||
      (userData.email?.[0] || "?").toUpperCase()
    : "";

  const iconButton =
    "relative flex h-11 w-11 items-center justify-center rounded-xl border " +
    "border-bgray-200 text-bgray-700 transition-colors hover:bg-bgray-100 " +
    "dark:border-darkblack-400 dark:text-bgray-50 dark:hover:bg-darkblack-500";

  return (
    <header className="sticky top-0 z-20 border-b border-bgray-200 bg-white dark:border-darkblack-400 dark:bg-darkblack-600">
      <div className="flex h-[72px] w-full items-center justify-between gap-4 px-4 sm:px-6 xl:px-8">
        {/* Left: menu toggle + page title */}
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onMenuClick}
            title="Toggle menu"
            aria-label="Toggle menu"
            className={iconButton}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M3 5H17M3 10H17M3 15H17"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-bgray-900 dark:text-bgray-50 lg:text-2xl">
              {page.title}
            </h3>
            <p className="hidden truncate text-sm font-medium text-bgray-600 dark:text-bgray-50 sm:block">
              {page.subtitle}
            </p>
          </div>
        </div>

        {/* Right: theme, notifications, profile */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className={iconButton}
          >
            <span className="block dark:hidden">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18.3284 14.8687C13.249 14.8687 9.13135 10.751 9.13135 5.67163C9.13135 4.74246 9.26914 3.84548 9.5254 3C5.74897 4.14461 3 7.65276 3 11.803C3 16.8824 7.11765 21 12.197 21C16.3472 21 19.8554 18.251 21 14.4746C20.1545 14.7309 19.2575 14.8687 18.3284 14.8687Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="hidden dark:block">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 20V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M20.6602 7L18.9281 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M5.07178 16L3.33973 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M3.33984 7L5.07189 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M18.9282 16L20.6603 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </span>
          </button>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              type="button"
              onClick={notificationAction}
              aria-label="Notifications"
              aria-expanded={notification}
              className={iconButton}
            >
              {noficationData?.length > 0 && (
                <span className="absolute -right-[3px] -top-[3px] h-3 w-3 rounded-full border-2 border-white bg-success-300 dark:border-darkblack-600" />
              )}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {notification && (
              <div className="drop-shadow absolute right-0 top-[56px] z-30 w-[calc(100vw-2rem)] max-w-[380px] overflow-hidden rounded-lg border border-bgray-200 bg-white dark:border-darkblack-400 dark:bg-darkblack-600">
                <div className="flex h-[60px] items-center justify-between border-b border-bgray-200 px-5 dark:border-darkblack-400">
                  <h3 className="text-lg font-bold text-bgray-900 dark:text-white">
                    Notifications
                  </h3>
                  <button type="button" onClick={notificationAction} aria-label="Close notifications">
                    <AiOutlineClose color="#A0AEC0" />
                  </button>
                </div>

                <ul className="scroll-style-1 max-h-[335px] w-full overflow-y-auto">
                  {noficationData ? (
                    noficationData.length > 0 ? (
                      noficationData.map((item, index) => (
                        <li
                          className="border-b border-bgray-200 px-5 py-4 last:border-0 hover:bg-bgray-100 dark:border-darkblack-400 dark:hover:bg-darkblack-500"
                          key={`${index}`}
                        >
                          <p className="mb-1 text-sm font-medium text-bgray-600 dark:text-bgray-50">
                            <strong className="text-bgray-900 dark:text-white">
                              {item.subject}{" "}
                            </strong>
                            {item.body}
                          </p>
                          <span className="text-xs font-medium text-bgray-500">
                            {dayjs(Number(item.created_time)).fromNow()}
                          </span>
                        </li>
                      ))
                    ) : (
                      // Previously an endless skeleton, which read as "still
                      // loading" even when the server said there was nothing.
                      <li className="px-5 py-10 text-center text-sm text-bgray-500">
                        You have no notifications.
                      </li>
                    )
                  ) : (
                    <li className="px-5 py-4">
                      <Skeleton count={3} height={40} />
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <button
              type="button"
              onClick={profileAction}
              aria-expanded={profileActionShow}
              className="flex items-center gap-3 rounded-xl p-1 transition-colors hover:bg-bgray-100 dark:hover:bg-darkblack-500"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-success-300 text-sm font-bold text-white">
                {initials}
              </span>
              <span className="hidden text-left 2xl:block">
                <span className="block text-sm font-bold leading-5 text-bgray-900 dark:text-white">
                  {userData?.first_name} {userData?.last_name}
                </span>
                <span className="block text-xs font-medium text-bgray-600 dark:text-bgray-50">
                  {userData?.email}
                </span>
              </span>
            </button>

            {profileActionShow && (
              <div className="drop-shadow absolute right-0 top-[56px] z-30 w-[280px] overflow-hidden rounded-lg border border-bgray-200 bg-white dark:border-darkblack-400 dark:bg-darkblack-600">
                <div className="border-b border-bgray-200 px-4 py-3 dark:border-darkblack-400">
                  <p className="truncate text-sm font-semibold text-bgray-900 dark:text-white">
                    {userData?.first_name} {userData?.last_name}
                  </p>
                  <p className="truncate text-xs text-bgray-500">{userData?.email}</p>
                </div>
                <div className="p-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-success-300 transition-colors hover:bg-bgray-100 dark:hover:bg-darkblack-500"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M15 10L13.7071 11.2929C13.3166 11.6834 13.3166 12.3166 13.7071 12.7071L15 14M14 12L22 12M6 20C3.79086 20 2 18.2091 2 16V8C2 5.79086 3.79086 4 6 4M6 20C8.20914 20 10 18.2091 10 16V8C10 5.79086 8.20914 4 6 4M6 20H14C16.2091 20 18 18.2091 18 16M6 4H14C16.2091 4 18 5.79086 18 8"
                        stroke="#22C55E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className="text-sm font-semibold">Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
